/**
 * Score a source template against a vendor's existing `data` shape to hint
 * which template most likely built the vendor.
 *
 * Heuristic: +1 for every template section whose `id` matches a top-level
 * API subtree on the source where `.active === true`. Templates without
 * sections (or sections the vendor didn't activate) score 0. Ties preserve
 * input order, so the sort is stable.
 *
 * Used by the Edit drawer's pick-template fallback to bubble the
 * most-plausible template to the top of the picker when a legacy vendor
 * has no persisted `template_id` and its slugified `code` doesn't match
 * any template slug.
 *
 * Kept intentionally simple — a shape-match is a hint, not a guess. The
 * user still confirms the template; saving then writes `template_id`
 * permanently so this heuristic only runs once per legacy vendor.
 */
import type { SourceTemplate } from './sourceTemplates';

export interface ScoreableSource {
  alert?: { active?: boolean } | null;
  announce?: { active?: boolean } | null;
  client?: { active?: boolean } | null;
  event?: { active?: boolean } | null;
  stream?: { active?: boolean } | null;
  [key: string]: unknown;
}

export const scoreTemplateForSource = (
  template: SourceTemplate,
  source: ScoreableSource,
): number => {
  let score = 0;
  for (const section of template.sections ?? []) {
    const bucket = source[section.id];
    if (
      bucket &&
      typeof bucket === 'object' &&
      (bucket as { active?: unknown }).active === true
    ) {
      score += 1;
    }
  }
  return score;
};

/** Return a new array sorted by match score (desc), stable for ties. */
export const sortTemplatesBySourceMatch = (
  templates: readonly SourceTemplate[],
  source: ScoreableSource,
): SourceTemplate[] =>
  templates
    .map((template, index) => ({
      template,
      score: scoreTemplateForSource(template, source),
      index,
    }))
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map(({ template }) => template);
